import React, { useState, useEffect } from 'react';
import { Form, Table, Badge } from 'react-bootstrap';
import { fetchProcessedBankStatements } from '../../api/cases';
import { useAuth } from '../../contexts/AuthContext';
import { ProcessedBankStatement } from '../../constants/constants';
import { formatDate } from '../../utils/dateFormatters';

interface BankStatementGapInputsProps {
  case_id: string;
  onChange: (data: {
    dateRange: { from: string; to: string };
    includedStatements: Array<{ statement_id: string }>;
  }) => void;
  validated: boolean;
}

interface BankStatementGapInputs {
  dateRange: {
    from: string;
    to: string;
  };
  includedStatements: Array<{
    statement_id: string;  // Only send statement ID
  }>;
}

const BankStatementGapInputs: React.FC<BankStatementGapInputsProps> = ({ case_id, onChange, validated }) => {
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  const [statements, setStatements] = useState<ProcessedBankStatement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const auth = useAuth();

  // Helpers to avoid timezone issues and format YYYY-MM-DD
  const parseISODate = (iso: string | null | undefined): Date => {
    if (!iso) return new Date(0); // statements with no date sort first, never crash
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(Date.UTC(y, (m || 1) - 1, d || 1));
  };
  const formatISO = (d: Date): string => d.toISOString().split('T')[0];
  const firstOfMonth = (d: Date): Date => new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1));
  const lastOfMonth = (d: Date): Date => new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0));

  useEffect(() => {
    const loadStatements = async () => {
      try {
        const response = await fetchProcessedBankStatements(case_id, auth.user?.id_token);
        // Sort statements by date in ascending order
        const sortedStatements = response.data.statements.sort((a: ProcessedBankStatement, b: ProcessedBankStatement) => 
          parseISODate(a.statement_date).getTime() - parseISODate(b.statement_date).getTime()
        );
        setStatements(sortedStatements);

        // Auto-select date range spanning from the 1st of the first statement's month
        // to the last day of the last statement's month, if not already set.
        // Statements with a genuinely blank statement_date sort first (via the
        // epoch-0 fallback in parseISODate, there only to keep the sort from
        // crashing) - excluded here so one blank date doesn't drag the
        // auto-suggested range's start back to 1970.
        const datedStatements = sortedStatements.filter((s: ProcessedBankStatement) => !!s.statement_date);
        if (datedStatements.length > 0) {
          const first = parseISODate(datedStatements[0].statement_date);
          const last = parseISODate(datedStatements[datedStatements.length - 1].statement_date);
          const autoFrom = formatISO(firstOfMonth(first));
          const autoTo = formatISO(lastOfMonth(last));
          setDateRange(prev => prev.from && prev.to ? prev : { from: autoFrom, to: autoTo });
        }
      } catch (err) {
        setError('Failed to load bank statements');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadStatements();
  }, [case_id, auth.user?.id_token]);

  useEffect(() => {
    if (!dateRange.from || !dateRange.to) return;

    const includedStatements = statements.filter(stmt => {
      const stmtDate = parseISODate(stmt.statement_date);
      const rangeFrom = parseISODate(dateRange.from);
      const rangeTo = parseISODate(dateRange.to);
      return stmtDate.getTime() >= rangeFrom.getTime() && stmtDate.getTime() <= rangeTo.getTime();
    });

    // Send only the id, never the full statement object - fetchProcessedBankStatements
    // returns each statement's full transactions array too (ProcessedBankStatement's
    // declared type omits it, but the actual runtime objects carry it), and that was
    // getting stored verbatim in the analysis record. For a case with years of bank
    // statements that blows past DynamoDB's 400KB item limit ("Item size has exceeded
    // the maximum allowed size"). The analysis Lambda already re-fetches full statement
    // data server-side from just the ids (BankStatementGapAnalysis.py's get_statements).
    onChange({
      dateRange,
      includedStatements: includedStatements.map(stmt => ({ statement_id: stmt.statement_id })),
    });
  }, [dateRange.from, dateRange.to, statements]);

  if (loading) return <div>Loading bank statements...</div>;
  if (error) return <div className="text-danger">{error}</div>;

  return (
    <div>
      <Form.Group className="mb-4">
        <Form.Label>Analysis Date Range</Form.Label>
        <div className="row">
          <div className="col">
            <Form.Control
              type="date"
              value={dateRange.from}
              onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })}
              placeholder="From"
              required
              isInvalid={validated && !dateRange.from}
            />
          </div>
          <div className="col">
            <Form.Control
              type="date"
              value={dateRange.to}
              onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })}
              placeholder="To"
              required
              isInvalid={validated && !dateRange.to}
            />
          </div>
        </div>
      </Form.Group>

      <div className="mb-3">
        <h6>Available Bank Statements</h6>
        <Table striped bordered hover>
          <thead>
            <tr>
              <th>Bank</th>
              <th>Account</th>
              <th>Period</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {statements.map((stmt) => {
              const isIncluded = dateRange.from && dateRange.to && 
                new Date(stmt.statement_date) >= new Date(dateRange.from) &&
                new Date(stmt.statement_date) <= new Date(dateRange.to);

              return (
                <tr key={stmt.statement_id} className={isIncluded ? 'table-success' : undefined}>
                  <td>{stmt.bank_name}</td>
                  <td>...{stmt.account_number}</td>
                  <td>{formatDate(stmt.statement_date)}</td>
                  <td>
                    <Badge bg={isIncluded ? 'success' : 'secondary'}>
                      {isIncluded ? 'Included' : 'Not in range'}
                    </Badge>
                  </td>
                </tr>
              );
            })}
            {statements.length === 0 && (
              <tr>
                <td colSpan={4} className="text-center">
                  No processed bank statements available
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
    </div>
  );
};

export default BankStatementGapInputs; 